import { z } from 'zod';
// ── Graph sub-schemas ──
const pipelineNodeSchema = z.object({
    id: z.string().min(1).max(100),
    type: z.enum([
        'manualTrigger', 'cronTrigger', 'eventTrigger', 'webhookTrigger',
        'aiPrompt', 'aiDecision', 'toolCall',
        'condition', 'loop', 'errorHandler', 'transform', 'notify', 'webhookOutput',
    ]),
    position: z.object({ x: z.number(), y: z.number() }),
    data: z.object({
        label: z.string().min(1).max(200),
        config: z.record(z.unknown()).default({}),
        description: z.string().max(500).optional(),
    }),
});
const pipelineEdgeSchema = z.object({
    id: z.string().min(1).max(100),
    source: z.string().min(1),
    target: z.string().min(1),
    sourceHandle: z.string().optional(),
    targetHandle: z.string().optional(),
    label: z.string().max(200).optional(),
    animated: z.boolean().optional(),
});
const pipelineGraphSchema = z.object({
    nodes: z.array(pipelineNodeSchema).max(200),
    edges: z.array(pipelineEdgeSchema).max(500),
    viewport: z.object({ x: z.number(), y: z.number(), zoom: z.number() }).optional(),
});
const pipelineVariableSchema = z.object({
    name: z.string().min(1).max(100),
    type: z.enum(['string', 'number', 'boolean', 'object', 'array']),
    defaultValue: z.unknown().optional(),
    description: z.string().max(500).optional(),
});
const triggerConfigSchema = z.object({
    type: z.enum(['manual', 'cron', 'event', 'webhook', 'pipeline']),
    cron: z.object({ schedule: z.string().max(100) }).optional(),
    event: z.object({ eventType: z.string(), filter: z.record(z.unknown()).optional() }).optional(),
    webhook: z.object({ secret: z.string().optional() }).optional(),
}).nullable().optional();
// ── Pipeline CRUD ──
export const pipelineCreateSchema = z.object({
    name: z.string().min(1).max(200),
    slug: z.string().min(1).max(200).regex(/^[a-z0-9-]+$/).optional(),
    description: z.string().max(1000).optional(),
    profileId: z.number().int().positive().nullable().optional(),
    graph: pipelineGraphSchema.optional(),
    variables: z.array(pipelineVariableSchema).max(50).optional(),
    triggerConfig: triggerConfigSchema,
    status: z.enum(['draft', 'active', 'archived']).optional(),
    isTemplate: z.boolean().optional(),
    templateCategory: z.string().max(50).optional(),
});
export const pipelineUpdateSchema = z.object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(1000).optional(),
    profileId: z.number().int().positive().nullable().optional(),
    graph: pipelineGraphSchema.optional(),
    variables: z.array(pipelineVariableSchema).max(50).optional(),
    triggerConfig: triggerConfigSchema,
    status: z.enum(['draft', 'active', 'archived']).optional(),
});
// ── Run ──
export const pipelineRunCreateSchema = z.object({
    triggerType: z.enum(['manual', 'cron', 'event', 'webhook', 'pipeline']).optional(),
    triggerData: z.record(z.unknown()).optional(),
    debugMode: z.boolean().optional(),
    breakpoints: z.array(z.string().max(100)).max(200).optional(),
});
// ── Generate ──
export const pipelineGenerateSchema = z.object({
    prompt: z.string().min(1).max(5000),
    profileId: z.number().int().positive().optional(),
});
// ── Query & Params ──
export const pipelineListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(20),
    search: z.string().max(200).optional(),
    status: z.enum(['draft', 'active', 'archived']).optional(),
    isTemplate: z.coerce.boolean().optional(),
});
export const pipelineIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
});
export const pipelineRunIdParamSchema = z.object({
    id: z.coerce.number().int().positive(),
    runId: z.coerce.number().int().positive(),
});
export const pipelineRunListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    perPage: z.coerce.number().int().min(1).max(100).optional().default(20),
    status: z.enum(['pending', 'running', 'paused', 'completed', 'failed', 'cancelled']).optional(),
});
//# sourceMappingURL=pipeline.schema.js.map